import { memo, useEffect, useCallback, useState } from "react";
import Form from "../../components/Form.js";
import Table from "../../components/Table.js";
import { useSnackbar } from "../../utils/index.js";
import Spinner from "../../components/Spinner.js";
import { Box, Typography, Grid2, Button } from "@mui/material";
import { getOwners, getLambScrapies, deleteLambScrapie } from "../../api/index.js";
import useSelectStyles from "../../components/styles/Select.js";
import {
    lambSlugHeaderNoFilterable,
    damCodeHeaderNoClick,
    sireCodeHeaderNoClick,
    sexHeaderNoFilter,
    birthDateHeaderNotFilterable,
    sampleNumberHeader,
    firstResultHeader,
    secondResultHeader,
    sampleDateHeader,
    resultDateHeader,
} from "../../components/TableHeaders.js";
import { SecondaryBackgroundButton } from "../../components/Buttons.js";

const DeleteLambScrapie = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [fetchedLambScrapies, setFetchedLambScrapies] = useState([]);
    const [lambScrapiesDropdownValues, setLambScrapiesDropdownValues] = useState([]);
    const [selectedLambScrapie, setSelectedLambScrapie] = useState(null);
    const [step, setStep] = useState(1);
    const [scrapieCds, setScrapieCds] = useState([]);

    const selectClasses = useSelectStyles();
    const { success, error } = useSnackbar();

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { owners } = await getOwners();
            setFetchedOwners(owners);
            setOwnersDropdownValues(
                owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                }))
            );
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const selectOwnerDatesFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "date-picker",
            id: "startLambingDate",
            type: "desktop",
            label: "Έναρξη περιόδου τοκετών",
            width: "595px",
            background: "third",
            required: true,
        },
        {
            customType: "date-picker",
            id: "endLambingDate",
            type: "desktop",
            label: "Τέλος περιόδου τοκετών",
            width: "595px",
            background: "third",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ];

    const selectLambFormContent = [
        {
            customType: "dropdown",
            id: "lamb",
            items: lambScrapiesDropdownValues,
            placeholder: "Επιλογή αμνού",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Επιλογή",
        },
    ];

    const scrapiesTableColumns = [
        lambSlugHeaderNoFilterable,
        damCodeHeaderNoClick,
        sireCodeHeaderNoClick,
        sexHeaderNoFilter,
        birthDateHeaderNotFilterable,
        sampleNumberHeader,
        firstResultHeader,
        secondResultHeader,
        sampleDateHeader,
        resultDateHeader,
    ];

    const handleOwnerDateSelect = async (values) => {
        setIsLoading(true);
        const selectedOwnerObj = fetchedOwners.find(
            (owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner
        );

        const startLambingDate = values.startLambingDate;
        const endLambingDate = values.endLambingDate;
        const { success: scs, scrapies, scrapieCodes, message } = await getLambScrapies(
            selectedOwnerObj?._id,
            startLambingDate,
            endLambingDate,
        );
        if (scs) {
            setFetchedLambScrapies(scrapies);
            setLambScrapiesDropdownValues(
                scrapies.map((scrapie) => ({
                    value: scrapie?.lambSlug,
                    text: scrapie?.lambSlug,
                }))
            );
            setScrapieCds(scrapieCodes);
            setStep(2);
        } else {
            error(message);
        }
        setIsLoading(false);
    };

    const handleLambSelect = (values) => {
        const selectedLambScrapie = fetchedLambScrapies.find(
            (scrapie) => scrapie.lambSlug === values.lamb
        );

        const tableData = {
            _id: selectedLambScrapie?._id,
            lambSlug: selectedLambScrapie?.lambSlug,
            damCode: selectedLambScrapie?.animalId?.damCode,
            sireCode: selectedLambScrapie?.animalId?.sireCode,
            sex: selectedLambScrapie?.animalId?.sex,
            birthDate: selectedLambScrapie?.animalId?.birthDate,
            sampleCode: selectedLambScrapie?.sampleCode,
            sampleDate: selectedLambScrapie?.sampleDate,
            resultDate: selectedLambScrapie?.resultDate,
            result1: scrapieCds.find((code) => Number(code.scrapieCode) === selectedLambScrapie?.result1)?.scrapieName,
            result2: scrapieCds.find((code) => Number(code.scrapieCode) === selectedLambScrapie?.result2)?.scrapieName,
        }

        setSelectedLambScrapie(tableData);
    };

    const handleDeleteScrapie = async (scrapieId) => {
        setIsLoading(true);
        const { success: scs, message } = await deleteLambScrapie(scrapieId);
        if (scs) {
            success(message);
            const remainingScrapies = fetchedLambScrapies.filter((scrapie) => scrapie._id !== scrapieId);
            setFetchedLambScrapies(remainingScrapies);
            setLambScrapiesDropdownValues(
                remainingScrapies.map((scrapie) => ({
                    value: scrapie?.lambSlug,
                    text: scrapie?.lambSlug,
                }))
            );
            setSelectedLambScrapie(null);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    return (
        <>
            <Spinner open={isLoading} />
            {step === 1 && (
                <Box className={selectClasses.outerBox}>
                    <Box container flexDirection="column" className={selectClasses.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Επιλέξτε την Εκτροφή και τις ημερομηνίες που σας ενδιαφέρουν:
                        </Typography>
                        <Form
                            content={selectOwnerDatesFormContent}
                            onSubmit={handleOwnerDateSelect}
                            toResetForm={true}
                        />
                    </Box>
                </Box>
            )}
            {step === 2 && (
                <>
                    <Form
                        content={selectLambFormContent}
                        onSubmit={handleLambSelect}
                        toResetForm={true}
                    />
                    {selectedLambScrapie && (
                        <Grid2 container spacing={2} sx={{ marginTop: "1rem", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", mt: "2rem" }}>
                            <Grid2 item xs={12} md={12}>
                                <Table
                                    data={[selectedLambScrapie]}
                                    noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν πληροφορίες για το πρόβατο"}
                                    columns={scrapiesTableColumns}
                                    showPageSizeOptions={false}
                                />
                            </Grid2>
                            {step !== 1 && (
                                <Box sx={{ width: "100%", marginTop: null, display: "flex", justifyContent: "center" }}>
                                    <SecondaryBackgroundButton
                                        variant="outlined"
                                        onClick={() => handleDeleteScrapie(selectedLambScrapie?._id)}
                                        title="Διαφραφή"
                                    />
                                </Box>
                            )}
                        </Grid2>
                    )}
                </>
            )}
            {step !== 1 && (
                <Box sx={{ width: "100%", marginTop: "2rem", display: "flex", justifyContent: "center" }}>
                    <Button variant="outlined" color="secondary" onClick={() => setStep(step - 1)}>
                        Πίσω
                    </Button>
                </Box>
            )}
        </>
    )
}

export default memo(DeleteLambScrapie);